import { META_PIXEL_ID, type MetaStandardEvent } from "./config";

export type MetaClickIds = {
    eventId: string;
    fbp?: string;
    fbc?: string;
    eventSourceUrl: string;
};

export type MetaLeadPii = {
    email?: string;
    phone?: string;
    name?: string;
};

export type MetaEventParams = Record<string, unknown>;

const LEAD_STORAGE_KEY = "flexpro_meta_lead";
const ONCE_PREFIX = "flexpro_meta_once_";

function readCookie(name: string) {
    if (typeof document === "undefined") return "";
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match?.[1] ? decodeURIComponent(match[1]) : "";
}

function writeCookie(name: string, value: string, maxAgeDays = 90) {
    const maxAge = maxAgeDays * 24 * 60 * 60;
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function createEventId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
        crypto.getRandomValues(bytes);
    } else {
        for (let i = 0; i < bytes.length; i += 1) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
    }

    const versionByte = bytes[6] ?? 0;
    const variantByte = bytes[8] ?? 0;
    bytes[6] = (versionByte & 0x0f) | 0x40;
    bytes[8] = (variantByte & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function persistFacebookClickId() {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");
    if (!fbclid) return;
    const existing = readCookie("_fbc");
    if (existing.includes(fbclid)) return;
    writeCookie("_fbc", `fb.1.${Date.now()}.${fbclid}`);
}

export function persistMetaLead(lead: MetaLeadPii) {
    if (typeof window === "undefined") return;
    const current = readMetaLead();
    const next = {
        email: lead.email?.trim() || current.email,
        phone: lead.phone?.trim() || current.phone,
        name: lead.name?.trim() || current.name,
    };
    sessionStorage.setItem(LEAD_STORAGE_KEY, JSON.stringify(next));
    enhancePixelMatch(next);
}

export function readMetaLead(): MetaLeadPii {
    if (typeof window === "undefined") return {};
    try {
        const raw = sessionStorage.getItem(LEAD_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as MetaLeadPii;
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

export function enhancePixelMatch(lead: MetaLeadPii = readMetaLead()) {
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    const em = lead.email?.trim().toLowerCase();
    const ph = lead.phone?.replace(/\D/g, "");
    const parts = lead.name?.trim().split(/\s+/).filter(Boolean) ?? [];
    const advanced: Record<string, string> = { country: "br" };
    if (em) advanced.em = em;
    if (ph) advanced.ph = ph.startsWith("55") ? ph : ph.length >= 10 ? `55${ph}` : ph;
    if (parts[0]) advanced.fn = parts[0].toLowerCase();
    if (parts.length > 1) advanced.ln = (parts[parts.length - 1] ?? "").toLowerCase();
    if (!advanced.em && !advanced.ph) return;
    window.fbq("init", META_PIXEL_ID, advanced);
}

export function getMetaClickIds(): MetaClickIds {
    persistFacebookClickId();
    return {
        eventId: createEventId(),
        fbp: readCookie("_fbp") || undefined,
        fbc: readCookie("_fbc") || undefined,
        eventSourceUrl: window.location.href,
    };
}

export function trackPixelEvent(eventName: string, eventId: string, params: MetaEventParams = {}) {
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    window.fbq("track", eventName, params, { eventID: eventId });
}

export async function sendCapiFromBrowser(
    eventName: string,
    meta: MetaClickIds,
    extra?: MetaLeadPii,
    customData?: MetaEventParams,
) {
    try {
        await fetch("/api/meta/capi", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                eventName,
                eventId: meta.eventId,
                fbp: meta.fbp,
                fbc: meta.fbc,
                eventSourceUrl: meta.eventSourceUrl,
                email: extra?.email,
                phone: extra?.phone,
                name: extra?.name,
                customData,
            }),
            keepalive: true,
        });
    } catch (error) {
        console.error("Meta CAPI browser send failed:", error);
    }
}

export function trackMetaEvent(eventName: MetaStandardEvent | string, extra?: MetaLeadPii, params: MetaEventParams = {}) {
    const meta = getMetaClickIds();
    const lead = { ...readMetaLead(), ...extra };
    enhancePixelMatch(lead);
    trackPixelEvent(eventName, meta.eventId, params);
    void sendCapiFromBrowser(eventName, meta, lead, params);
    return meta;
}

export function trackMetaEventOnce(
    key: string,
    eventName: MetaStandardEvent | string,
    extra?: MetaLeadPii,
    params: MetaEventParams = {},
) {
    if (typeof window === "undefined") return;
    const storageKey = `${ONCE_PREFIX}${key}`;
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, "1");
    return trackMetaEvent(eventName, extra, params);
}
